import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes/public";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * JUB-226 / JUB-100: Public Privacy Policy page covering controller vs.
 * processor roles, sub-processors (Stripe Payments Europe Ltd., Twilio
 * SendGrid), retention windows (30 days account / 14 days event / 10 years
 * anonymized accounting records per Law 82/1991 Art. 25), and ANSPDCP rights.
 */
export default function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();

  usePageMeta({
    title: t("privacyPage.metaTitle"),
    description: t("privacyPage.metaDescription"),
    locale,
    path: PUBLIC_ROUTES.privacy(locale),
    alternates: { ro: PUBLIC_ROUTES.privacy("ro"), en: PUBLIC_ROUTES.privacy("en") },
  });

  return (
    <PublicContentLayout>
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl" data-testid="privacy-page-title">{t("privacyPage.title")}</CardTitle>
          <CardDescription>{t("privacyPage.lastUpdated")}</CardDescription>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none dark:prose-invert space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-2">{t("privacyPage.controller.title")}</h2>
            <p>{t("privacyPage.controller.content")}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">{t("privacyPage.dataProcessed.title")}</h2>
            <p>{t("privacyPage.dataProcessed.content")}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">{t("privacyPage.processors.title")}</h2>
            <p>{t("privacyPage.processors.content")}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">{t("privacyPage.retention.title")}</h2>
            <p>{t("privacyPage.retention.content")}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">{t("privacyPage.rights.title")}</h2>
            <p>{t("privacyPage.rights.content")}</p>
          </section>
        </CardContent>
      </Card>
    </PublicContentLayout>
  );
}
